// Copyright (c) 2022 by Move Closer

import uniqBy from 'lodash/uniqBy'

import { ImageProps } from '../../../dsl/atoms/Image'
import { SrcSet } from '../../../dsl/composables/src-set'

import { AllowedImageRatio, ImageData, ImageVariant } from '../../../contexts'

/**
 * Maps the passed-in `ImageFile` to an object implementing the `BootstrapImageProps` interface.
 *
 * @param imageData - The `ImageData` to map.
 * @param ratio - image ratio form predefined map
 * @param maxWidth {number} - image max width in pixels
 */
export const toImageProps = (
  imageData: ImageData,
  ratio: AllowedImageRatio | null = AllowedImageRatio.Original,
  maxWidth?: number
): ImageProps => {
  // Prepare the fallback object in case of any errors.
  let errorReturn: ImageProps = { alt: '', /* author: '', caption: '', */ src: '', srcset: '', title: '', isLazy: true }

  // Check if the provided `imageData` object contains its `srcset` property.
  if (
    typeof imageData === 'undefined' ||
    imageData === null
    // (
    //   imageFile.preset !== ImageFilePreset.Logo &&
    //   // FIXME - commented because condition form line 70 secure this conditions and otherwise can't get image preview in CMS PageBuilder
    //   (!Object.prototype.hasOwnProperty.call(imageFile, 'srcset') ||
    //   typeof imageFile.srcset !== 'object' ||
    //   imageFile.srcset === null ||
    //   Object.keys(imageFile.srcset).length === 0)
    // )
  ) {
    console.error('toImageProps(): Broken [imageFile] object!\nFull [imageFile] object:', imageData)
    return errorReturn
  }

  // Extract the required keys.
  const { alt = '', /* author = '', caption = '', */ htmlTitle: title = imageData.title ?? null, isLazy } = imageData

  // Update the fallback object with new data.
  errorReturn = { ...errorReturn, alt, /* author, caption, */ title }

  // // Resolve the current screen resolution.
  // const screenResolution: number = typeof window === 'undefined' ? 1920 : window.innerWidth
  //
  // // Resolve the available resolutions for the given image.
  // let availableResolutions: number[]
  // try {
  //   availableResolutions = Object.keys(imageFile.srcset).map(_ => +_)
  // } catch (error) {
  //   console.error('prepareImage(): Failed to resolve the list of available screen/image resolutions!', error)
  //   return errorReturn
  // }
  //
  // // If the `ImageFile` has no resolutions specified,
  // // return the `Image` with an empty `src` property.
  // if (availableResolutions.length === 0) {
  //   console.warn(
  //     `prepareImage(): The [srcset] property of the [ImageFile] of ID [${id}] is EMPTY!\nFull [ImageFile] object:`, imageFile
  //   )
  //   return errorReturn
  // }
  //
  // // Resolve the nearest available resolution to the current screen resolution.
  // // @see https://stackoverflow.com/a/19277804/11869579
  // // @author Joe Grund <grundjoseph@gmail.com>
  // const nearestResolution: number = availableResolutions.reduce((prev, curr) => {
  //   return (Math.abs(curr - screenResolution) < Math.abs(prev - screenResolution) ? curr : prev)
  // })

  /**
   * Value for the `<img>'s` `[src]` attribute.
   */
  let src: string

  if (
    typeof imageData.srcset === 'undefined' ||
    !Array.isArray(imageData.srcset) ||
    imageData.srcset.length === 0
  ) {
    try {
      src = imageData.path ? (process.env.VUE_APP_IMAGE_PATH + imageData.path) : (typeof imageData.url === 'undefined' ? imageData.src : imageData.url)
    } catch (e) {
      console.warn(e)
      src = ''
    }

    // Return the complete object.
    return { alt, /* author, caption, */ height: imageData.height, src, title, isLazy, width: imageData.width }
  }

  // Let's found a specific ratio.
  let found: ImageVariant<string>[] = []
  if (ratio !== null) {
    found = imageData.srcset.filter(v => {
      return (v.ratio || AllowedImageRatio.Original) === ratio
    })
  }

  let sources: ImageVariant<string>[] = found.length > 0 ? found : imageData.srcset
  sources.sort((a, b) => a.width - b.width)

  // filter out images that are too big
  if (typeof maxWidth === 'number') {
    const smallestImage = sources[0]
    sources = sources.filter(({ width }) => width <= maxWidth)

    if (sources.length === 0) { // no image smaller than maxWidth found, take the smallest one
      sources = [smallestImage]
    }
  }

  sources = uniqBy(sources ?? [], 'width')

  let height: number | undefined
  let width: number | undefined

  try {
    const middle: ImageVariant<string> = sources[Math.floor(sources.length / 2)]
    src = imagePath(middle)
    height = middle.height ?? undefined
    width = middle.width
  } catch (error) {
    console.error('toBootstrapImageProps(): Failed to resolve the applicable [src]!', error)
    return errorReturn
  }

  // Resolve the value for the `<img>'s` `[srcset]` attribute.
  let srcset: SrcSet | undefined

  try {
    if (sources.length > 1) {
      srcset = sources.reduce<SrcSet>((acc, curr) => ({
        ...acc, [curr.width]: imagePath(curr)
      }), {})
    }
  } catch (error) {
    console.error('toBootstrapImageProps(): Failed to construct the [srcset]!', error)
  }

  // Return the complete object.
  return { alt, /* author, caption, */ height, src, srcset, title, width, isLazy }
}

const imagePath = (imageVariant: ImageVariant<string>): string => {
  if (!imageVariant.path) {
    return imageVariant.url ?? ''
  }

  return process.env.VUE_APP_IMAGE_PATH + imageVariant.path
}
